import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { LoadingContext } from '../LoadingContext';
import { CartContext } from '../CartContext';
import Dialog from '@mui/material/Dialog';
import { getProductPath } from '../productUrls';
import Swal from 'sweetalert2';
import './PromotionDetail.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Detail page for a standalone promotion item. These live only in PromotionProducts -
 * they are not catalogue products - so they have no /product/{code} page of their own.
 * Promotions that are linked to a catalogue product are redirected to that product
 * instead, so there is only ever one detail page per item.
 */
function PromotionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setIsLoading } = useContext(LoadingContext);
  const { addToCart } = useContext(CartContext);

  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // The public endpoint returns every active promotion; pick this one out of it,
      // which also means an expired or deactivated promotion correctly 404s here.
      const res = await fetch(`${API_BASE_URL}/api/Promotions`);
      if (!res.ok) throw new Error('Failed to load');

      const list = await res.json();
      const found = (list || []).find((p) => String(p.id) === String(id));

      if (!found) {
        setNotFound(true);
        return;
      }

      // A linked promotion belongs on the catalogue product's own page
      if (!found.isStandalone && found.productCode) {
        navigate(getProductPath(found.productCode), { replace: true });
        return;
      }

      setItem(found);
      // Start at the minimum order quantity so the first click is always valid
      setQuantity(found.moq && found.moq > 1 ? found.moq : 1);
    } catch (e) {
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, setIsLoading]);

  useEffect(() => {
    load();
  }, [load]);

  if (notFound) {
    return (
      <div className="promo-detail-container">
        <div className="promo-detail-empty">
          <i className="fas fa-tag"></i>
          <p>This promotion is no longer available.</p>
          <Link to="/promotion" className="promo-detail-back-link">
            Back to Special Offers
          </Link>
        </div>
      </div>
    );
  }

  if (!item) return null;

  const gallery = [
    { id: 'main', imageUrl: item.imageUrl },
    ...(item.additionalImages || []),
  ].filter((img) => img && img.imageUrl);

  const original = item.originalPrice != null ? parseFloat(item.originalPrice) : null;
  const promo = parseFloat(item.promotionPrice);
  const discount =
    original && promo && promo < original
      ? Math.min(99, Math.round((1 - promo / original) * 100))
      : null;

  const inStock = item.stockQuantity == null || item.stockQuantity > 0;

  // MOQ sets the minimum; only use it as the step when enabled in admin.
  const minQuantity = item.moq && item.moq > 1 ? item.moq : 1;
  const step = item.isIncreaseByMOQ ? minQuantity : 1;

  const changeQuantity = (delta) => {
    setQuantity((prev) => {
      const next = prev + delta;
      if (next < minQuantity) return minQuantity;
      if (item.stockQuantity != null && next > item.stockQuantity) return prev;
      return next;
    });
  };

  const handleAddToCart = () => {
    addToCart(
      {
        promotionProductId: item.id,
        productID: null,
        name: item.productName,
        price: promo,
        imageUrl: item.imageUrl,
        moq: minQuantity,
        isIncreaseByMOQ: item.isIncreaseByMOQ ?? false,
        uom: item.uom || '',
        isRedeemable: false,
      },
      quantity
    );

    Swal.fire({
      icon: 'success',
      title: 'Added to Cart',
      text: `${quantity} x ${item.productName}`,
      timer: 1500,
      showConfirmButton: false,
    });
  };

  return (
    <div className="promo-detail-container">
      <div className="promo-detail-breadcrumb">
        <Link to="/promotion">Special Offers</Link>
        <span> / </span>
        <span>{item.productName}</span>
      </div>

      <div className="promo-detail-layout">
        <div className="promo-detail-gallery">
          <button
            type="button"
            className="promo-detail-main-image"
            onClick={() => setIsLightboxOpen(true)}
            disabled={!gallery[activeImage]}
            aria-label={`Enlarge ${item.productName} image`}
            title="Click to enlarge"
          >
            {discount && <span className="promo-detail-badge">-{discount}%</span>}
            <img
              key={gallery[activeImage]?.imageUrl}
              src={gallery[activeImage]?.imageUrl}
              alt={item.productName}
              onError={(e) => { e.target.style.visibility = 'hidden'; }}
            />
          </button>

          {gallery.length > 1 && (
            <div className="promo-detail-thumbs">
              {gallery.map((img, index) => (
                <button
                  type="button"
                  key={img.id ?? index}
                  className={`promo-detail-thumb ${index === activeImage ? 'is-active' : ''}`}
                  onClick={() => setActiveImage(index)}
                  aria-label={`View ${item.productName} image ${index + 1}`}
                  aria-pressed={index === activeImage}
                >
                  <img src={img.imageUrl} alt={`${item.productName} ${index + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="promo-detail-info">
          <h1 className="promo-detail-name">{item.productName}</h1>

          <div className="promo-detail-price-row">
            {original != null && (
              <span className="promo-detail-original">RM {original.toFixed(2)}</span>
            )}
            <span className="promo-detail-price">RM {promo.toFixed(2)}</span>
            {item.uom && <span className="promo-detail-uom">/ {item.uom}</span>}
          </div>

          <div className="promo-detail-meta">
            <div className="promo-detail-meta-row">
              <span className="promo-detail-meta-label">Availability</span>
              <span className={inStock ? 'promo-detail-in-stock' : 'promo-detail-out-stock'}>
                {inStock ? 'In Stock' : 'Out of Stock'}
              </span>
            </div>

            {item.moq > 1 && (
              <div className="promo-detail-meta-row">
                <span className="promo-detail-meta-label">Minimum Order</span>
                <span>{item.moq} {item.uom || 'unit(s)'}</span>
              </div>
            )}
          </div>

          {item.description && (
            <div className="promo-detail-description">
              <h2>Description</h2>
              <p>{item.description}</p>
            </div>
          )}

          {inStock ? (
            <div className="promo-detail-buy">
              <div className="promo-detail-qty">
                <span className="promo-detail-qty-label">Quantity</span>
                <div className="promo-detail-stepper">
                  <button type="button" onClick={() => changeQuantity(-step)} aria-label="Decrease">
                    &minus;
                  </button>
                  <span className="promo-detail-qty-value">{quantity}</span>
                  <button type="button" onClick={() => changeQuantity(step)} aria-label="Increase">
                    +
                  </button>
                </div>
                {step > 1 && (
                  <span className="promo-detail-qty-note">
                    Sold in multiples of {step}
                  </span>
                )}
              </div>

              <button type="button" className="promo-detail-cart-btn" onClick={handleAddToCart}>
                <i className="fas fa-shopping-cart"></i> Add to Cart
              </button>
            </div>
          ) : (
            <div className="promo-detail-enquiry">
              <p>This item is currently out of stock. Get in touch and we will let you know when it is back.</p>
              <Link to="/contact" className="promo-detail-enquiry-btn">
                Enquire Now
              </Link>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        maxWidth={false}
        className="promo-detail-lightbox"
        slotProps={{ paper: { 'aria-label': `Enlarged image of ${item.productName}` } }}
      >
        <button
          type="button"
          className="promo-detail-lightbox-close"
          onClick={() => setIsLightboxOpen(false)}
          aria-label="Close image preview"
        >
          &times;
        </button>
        <img
          className="promo-detail-lightbox-image"
          src={gallery[activeImage]?.imageUrl}
          alt={item.productName}
        />
        {gallery.length > 1 && (
          <div className="promo-detail-lightbox-thumbs">
            {gallery.map((img, index) => (
              <button
                type="button"
                key={img.id ?? index}
                className={`promo-detail-thumb ${index === activeImage ? 'is-active' : ''}`}
                onClick={() => setActiveImage(index)}
                aria-label={`View ${item.productName} image ${index + 1}`}
                aria-pressed={index === activeImage}
              >
                <img src={img.imageUrl} alt={`${item.productName} ${index + 1}`} />
              </button>
            ))}
          </div>
        )}
      </Dialog>
    </div>
  );
}

export default PromotionDetail;
